import React, { useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getCashSummary } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

const amountTable = (title, rows) => (
  <div className="table-scroll" style={{ marginBottom: 16 }}>
    <h3>{title}</h3>
    <table className="attendance-table">
      <thead>
        <tr>
          <th>Ledger</th>
          <th>Category</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.ledgerId}>
            <td>{r.ledgerName}</td>
            <td>{r.categoryName}</td>
            <td>{r.amount.toFixed(2)}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={3}>None</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/**
 * Direct port of digicampusbd's `general-accounts-report-cash-summary`
 * ("Transaction Summary" / "Cash Summary") — an indirect-method cash-flow
 * view: real Income vs. real Expense (operating), plus non-operating
 * Asset/Liability movement (both category_type in Asset/Liability, excluding
 * the cash-equivalent ledgers themselves), reconciled against the
 * cash-equivalent ledgers' own opening/closing balance for the period, with
 * any Contra (cash-to-cash transfer) vouchers shown separately for
 * transparency. Confirmed via `GeneralAccountReportService.cashSummary`'s
 * 5 native queries — new `GET /api/accounts/vouchers/reports/cash-summary`
 * mirrors them verbatim (reusing this app's pre-existing
 * `cashSummaryIncomeQuery`/`cashSummaryExpenseQuery`, which — despite their
 * dashboard-card names — already implement the old app's real Asset/
 * Liability and Expense list queries exactly).
 */
export default function CashSummaryPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setData(await getCashSummary(fromDate, toDate));
    } catch (err) {
      setError("Failed to load cash summary.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Cash Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        data && (
          <div>
            <p style={{ fontWeight: 800 }}>Opening Balance (cash-equivalent, before From Date): {data.totalOpeningBalance.toFixed(2)}</p>
            {amountTable("Income", data.incomeList)}
            {amountTable("Expense", data.expenseList)}
            <p>Operating Surplus/Deficit (Income − Expense): {data.operatingSurplusDeficit.toFixed(2)}</p>
            {amountTable("Asset/Liability Movement (non-operating)", data.assetLiabilitiesList)}
            <p>Net Cash Movement: {data.netCashMovement.toFixed(2)}</p>
            <div className="table-scroll" style={{ marginBottom: 16 }}>
              <h3>Contra (cash-to-cash transfers)</h3>
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Ledger</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactionContra.map((r, i) => (
                    <tr key={i}>
                      <td>{r.ledgerName}</td>
                      <td>{r.debit.toFixed(2)}</td>
                      <td>{r.credit.toFixed(2)}</td>
                    </tr>
                  ))}
                  {data.transactionContra.length === 0 && (
                    <tr>
                      <td colSpan={3}>None</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p style={{ fontWeight: 800 }}>Closing Balance (cash-equivalent, through To Date): {data.totalClosingBalance.toFixed(2)}</p>
          </div>
        )
      )}
    </MainLayout>
  );
}
